import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { OperationalPoint, getActiveDates } from '../store/useStore';
import { X, Edit2, MapPin, Star, QrCode, Plus, CalendarDays, ArrowRight, Trash2, Flame, Lock, Settings, Music, Search, Calendar } from 'lucide-react';
import { MultiSlotPicker } from './SlotPlannerView';
import { CategoryDrawer, groupPointsByCategory } from './CategoryDrawer';
import QRCode from 'react-qr-code';
import { ConfigModal } from './ConfigModal';
import { PointIcon } from './icons/PointIcon';

const AVAILABLE_ICONS = [
  { name: 'MapPin' },
  { name: 'Tent' },
  { name: 'Music' },
  { name: 'Utensils' },
  { name: 'Coffee' },
  { name: 'Car' },
  { name: 'Info' },
  { name: 'Ticket' },
  { name: 'Star' },
  { name: 'Shield' },
  { name: 'Store' },
  { name: 'PlugConnected' },
  { name: 'Drum' },
  { name: 'Soup' },
  { name: 'TruckElectric' },
  { name: 'Passport' },
  { name: 'Player' },
  { name: 'Heart' },
  { name: 'Camera' },
  { name: 'Wifi' },
  { name: 'Battery' },
  { name: 'Flame' },
  { name: 'Trash' },
  { name: 'Users' },
  { name: 'Cart' },
  { name: 'Bell' },
  { name: 'Magnifier' },
  { name: 'MessageCircle' },
  { name: 'Wallet' },
  { name: 'CreditCard' },
  { name: 'CurrencyDollar' },
  { name: 'Accessibility' },
  { name: 'BrandBagsFm' },
  { name: 'Banana' },
  { name: 'CameraOff' },
  { name: 'InfoCircle' },
  { name: 'UsersGroup' },
  { name: 'UserPlus' },
  { name: 'UserCheck' },
  { name: 'Logout' },
  { name: 'LetterP' },
  { name: 'ShieldCheck' },
  { name: 'Beer' },
  { name: 'Wine' },
  { name: 'Glasses' },
  { name: 'Radio' },
  { name: 'Tv' },
  { name: 'Key' },
  { name: 'Hammer' },
  { name: 'Bus' },
  { name: 'AlertTriangle' },
  { name: 'HeartHandshake' }
];

export function PointsConfig() {
  const { points, addPoint, updatePoint, deletePoint, currentUser, categories, shifts, team, deleteShift, effectiveCategoryId } = useVisibleStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [closedDrawers, setClosedDrawers] = useState<Record<string, boolean>>({});
  
  const filteredPoints = points.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const visiblePoints = filteredPoints;
  const festivalDates = getActiveDates(points);
  
  const defaultCategory = effectiveCategoryId ? effectiveCategoryId : (categories[0]?.id || '');
  const isArtistCategoryManager = effectiveCategoryId && categories.find(c => c.id === effectiveCategoryId)?.isArtistCategory === true;
  const canSeeStageOption = currentUser.role === 'admin' || isArtistCategoryManager;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<{ pointId: string; idx: number; start: string; end: string; requiredTeam: number; originalStart: string; originalEnd: string } | null>(null);
  const [initialPoint, setInitialPoint] = useState<Omit<OperationalPoint, 'id'>>({ name: '', categoryId: defaultCategory, requiredTeam: 1, openingHours: [], isRestricted: false });
  
  const [qrModalPoint, setQrModalPoint] = useState<OperationalPoint | null>(null);
  const [printPngUrl, setPrintPngUrl] = useState<string | null>(null);
  const [settingsPoint, setSettingsPoint] = useState<OperationalPoint | null>(null);
  const [multiSlotPickerOpen, setMultiSlotPickerOpen] = useState(false);
 
  const [newPoint, setNewPoint] = useState<Omit<OperationalPoint, 'id'>>({
    name: '',
    categoryId: defaultCategory,
    requiredTeam: 1,
    openingHours: [],
    isRestricted: false
  });

  const handleAddSlot = () => {
    let dateToUse = new Date().toISOString().split('T')[0];
    if (festivalDates.length > 0) {
       dateToUse = festivalDates[0];
    }
    setNewPoint({
      ...newPoint,
      openingHours: [...newPoint.openingHours, { start: `${dateToUse}T08:00`, end: `${dateToUse}T20:00` }]
    });
  };

  const submitNewPoint = () => {
    if (!newPoint.name.trim()) return;

    if (editingId && newPoint.isStage) {
      // Find all shifts currently planned at this point
      const pointShifts = shifts.filter(s => s.pointId === editingId);
      // Find shifts of non-artist team members at this point
      const nonArtistShifts = pointShifts.filter(s => {
        const member = team.find(st => st.id === s.teamId);
        return !member || !member.isArtist;
      });

      if (nonArtistShifts.length > 0) {
        const confirmMsg = `⚠️ Attention : ${nonArtistShifts.length} affectation(s) de team (non-artiste) existe(nt) actuellement sur ce point.\n\nSi vous marquez ce point comme Scène/Stage, tout ce team standard sera retiré (ses shifts seront définitivement supprimés).\n\nVoulez-vous continuer et forcer cette modification ?`;
        if (!window.confirm(confirmMsg)) {
          return; // Abort saving
        }
        
        // If confirmed, delete all non-artist shifts at this point
        nonArtistShifts.forEach(sh => {
          deleteShift(sh.id);
        });
      }
    }

    if (editingId) {
      updatePoint(editingId, { ...newPoint, categoryId: effectiveCategoryId ? effectiveCategoryId : newPoint.categoryId });
      setEditingId(null);
    } else {
      addPoint({ ...newPoint, categoryId: effectiveCategoryId ? effectiveCategoryId : newPoint.categoryId });
    }
    setNewPoint({ name: '', categoryId: defaultCategory, requiredTeam: 1, openingHours: [], descriptionMarkdown: '', isRestricted: false, isStage: false });
    setInitialPoint({ name: '', categoryId: defaultCategory, requiredTeam: 1, openingHours: [], descriptionMarkdown: '', isRestricted: false, isStage: false });
    setModalOpen(false);
  };

  const openNew = () => {
    const empty = { name: '', categoryId: defaultCategory, requiredTeam: 1, openingHours: [], descriptionMarkdown: '', isRestricted: false, isStage: false };
    setNewPoint(empty);
    setInitialPoint(empty);
    setEditingId(null);
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingId(null); };
  const isDirty = JSON.stringify(newPoint) !== JSON.stringify(initialPoint);

  // Auto-open modal when navigated from Setup Guide (mobile)
  useEffect(() => {
    const flag = sessionStorage.getItem('guide_auto_open');
    if (flag === 'new-point') {
      sessionStorage.removeItem('guide_auto_open');
      setTimeout(() => openNew(), 150);
    }
  }, []);


  const startEdit = (point: OperationalPoint) => {
    setEditingId(point.id);
    const data = {
      name: point.name,
      categoryId: point.categoryId || defaultCategory,
      requiredTeam: point.requiredTeam,
      openingHours: [...point.openingHours],
      descriptionMarkdown: point.descriptionMarkdown || '',
      iconName: point.iconName,
      isMealPoint: point.isMealPoint,
      swapEnabled: point.swapEnabled,
      unavailabilityEnabled: point.unavailabilityEnabled,
      isMapFocus: point.isMapFocus,
      isRestricted: point.isRestricted || false,
      isStage: point.isStage || false,
    };
    setNewPoint(data);
    setInitialPoint(data);
    setModalOpen(true);
  };

  const activeCategory = categories.find(c => c.id === (newPoint.categoryId || defaultCategory));
  const isArtistCategory = activeCategory?.isArtistCategory === true;

  return (
    <div className="p-3.5 sm:p-6 md:p-8 h-full flex flex-col overflow-hidden relative">
      {/* Background glow */}
      <div className="absolute top-[20%] left-[-10%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative z-10">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-2 sm:gap-3">
            <MapPin className="text-indigo-400 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
            <span className="truncate">Points Opérationnels</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2 max-w-2xl leading-relaxed">
            Gérez les zones du festival nécessitant du team et configurez leurs créneaux d'ouverture.
          </p>
        </div>
        <button onClick={openNew} className="w-full sm:w-auto flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 sm:py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold text-xs sm:text-sm rounded-xl shadow-lg shadow-indigo-500/20 transition-all">
          <Plus className="w-4 h-4 flex-shrink-0" />
          <span>Nouveau Point</span>
        </button>
      </div>

      <div className="flex flex-col flex-1 min-h-0 relative z-10">
        {/* Point Form Modal */}
        <ConfigModal
          isOpen={modalOpen}
          onClose={closeModal}
          title={editingId ? 'Modifier le Point' : 'Nouveau Point Opérationnel'}
          icon={editingId ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          isDirty={isDirty}
        >
          <div className="p-6 space-y-5">
            {/* Category (admin only) */}
            {currentUser.role === 'admin' && (
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Catégorie d'appartenance</label>
                <select className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all appearance-none"
                  value={newPoint.categoryId || ''} onChange={(e) => {
                    const catId = e.target.value;
                    const catObj = categories.find(c => c.id === catId);
                    const isArtist = catObj?.isArtistCategory === true;
                    setNewPoint({ 
                      ...newPoint, 
                      categoryId: catId,
                      isStage: isArtist ? newPoint.isStage : false,
                      isMapFocus: isArtist ? false : newPoint.isMapFocus
                    });
                  }}>
                  <option value="">Sélectionnez une catégorie...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Nom du Point</label>
              <input autoFocus type="text"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all placeholder-slate-600"
                placeholder="Ex: Entrée VIP"
                value={newPoint.name} onChange={e => setNewPoint({...newPoint, name: e.target.value})} />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Icône Représentative</label>
              <div className="grid grid-cols-6 gap-2 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139] max-h-48 overflow-y-auto custom-scrollbar">
                {AVAILABLE_ICONS.map(({name}) => (
                  <button key={name} onClick={() => setNewPoint({...newPoint, iconName: name})}
                    className={`p-2.5 flex justify-center items-center rounded-lg transition-all hover:scale-110 ${newPoint.iconName === name || (!newPoint.iconName && name === 'MapPin') ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                    title={name}>
                    <PointIcon name={name} size={18} />
                  </button>
                ))}
              </div>
            </div>

            {/* Required team */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Team Requis (Par défaut)</label>
              <input type="number" min="1" max="50"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all"
                value={newPoint.requiredTeam} onChange={e => setNewPoint({...newPoint, requiredTeam: parseInt(e.target.value) || 1})} />
                      {/* Advanced Settings */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <label className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${newPoint.isRestricted ? 'bg-rose-500/10 border-rose-500/30' : 'bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700'}`}>
                <input type="checkbox" checked={newPoint.isRestricted} onChange={e => setNewPoint({...newPoint, isRestricted: e.target.checked})} className="sr-only" />
                <Lock className={`w-4 h-4 ${newPoint.isRestricted ? 'text-rose-400' : 'text-slate-500'}`} />
                <div className="flex flex-col">
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${newPoint.isRestricted ? 'text-rose-400' : 'text-slate-400'}`}>Accès Restreint</span>
                  <span className="text-[9px] text-slate-500">Masque ce point sensible sur la carte. Seuls ceux qui y sont affectés le verront.</span>
                </div>
              </label>

              {isArtistCategory && canSeeStageOption ? (
                <label className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${newPoint.isStage ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700'}`}>
                  <input type="checkbox" checked={newPoint.isStage} onChange={e => setNewPoint({...newPoint, isStage: e.target.checked})} className="sr-only" />
                  <Music className={`w-4 h-4 ${newPoint.isStage ? 'text-amber-400' : 'text-slate-500'}`} />
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${newPoint.isStage ? 'text-amber-400' : 'text-slate-400'}`}>Scène / Stage</span>
                    <span className="text-[9px] text-slate-500">Définit ce point comme scène. Seuls les artistes y seront affectés.</span>
                  </div>
                </label>
              ) : (
                <label className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${newPoint.isMapFocus ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700'}`}>
                  <input type="checkbox" checked={newPoint.isMapFocus} onChange={e => setNewPoint({...newPoint, isMapFocus: e.target.checked})} className="sr-only" />
                  <Star className={`w-4 h-4 ${newPoint.isMapFocus ? 'text-indigo-400' : 'text-slate-500'}`} />
                  <div className="flex flex-col">
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${newPoint.isMapFocus ? 'text-indigo-400' : 'text-slate-400'}`}>Focus Map</span>
                    <span className="text-[9px] text-slate-500">Mise en avant sur le live</span>
                  </div>
                </label>
              )}
            </div>        </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Instructions pour le team (Markdown)</label>
              <textarea rows={3}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all text-sm placeholder-slate-600 resize-none"
                placeholder={"Ex: - Veste obligatoire\n- Se présenter à l'entrée B"}
                value={newPoint.descriptionMarkdown || ''} onChange={e => setNewPoint({ ...newPoint, descriptionMarkdown: e.target.value })} />
            </div>

            {/* Opening hours */}
            <div className="pt-2">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> Créneaux d'Ouverture
                </label>
                <div className="flex gap-1.5">
                  <button onClick={handleAddSlot} className="text-xs bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 px-2 py-1 rounded transition-colors font-medium">+ Ajouter</button>
                  <button onClick={() => setMultiSlotPickerOpen(true)} className="text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded transition-colors font-medium flex items-center gap-1" title="Ajouter des créneaux sur plusieurs dates">
                    <Calendar className="w-3 h-3" /> Multi-dates
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {newPoint.openingHours.map((slot, idx) => (
                  <div key={idx} className="bg-[#0A0B0E]/80 border border-[#2D3139] rounded-xl p-4 flex flex-col space-y-3 relative group">
                    <button onClick={() => setNewPoint({...newPoint, openingHours: newPoint.openingHours.filter((_, i) => i !== idx)})}
                      className="absolute top-3 right-3 text-slate-500 hover:text-red-400 opacity-50 group-hover:opacity-100 transition-opacity bg-[#1A1D23] p-1 rounded">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex gap-3 pr-6">
                      <div className="flex-1">
                        <span className="text-[10px] text-slate-500 uppercase font-medium">Début</span>
                        <input type="datetime-local" value={slot.start}
                          onChange={(e) => { 
                            const s = [...newPoint.openingHours]; 
                            const newStart = e.target.value;
                            s[idx].start = newStart;
                            
                            // Auto-sync end date if it was matching the old start date or if it's empty/wrong day
                            if (newStart && s[idx].end) {
                              const startDate = newStart.split('T')[0];
                              const endParts = s[idx].end.split('T');
                              s[idx].end = `${startDate}T${endParts[1] || '00:00'}`;
                            } else if (newStart && !s[idx].end) {
                              s[idx].end = `${newStart.split('T')[0]}T23:59`;
                            }
                            
                            setNewPoint({...newPoint, openingHours: s}); 
                          }}
                          className="w-full bg-[#14161B] border border-[#2D3139] rounded p-2 mt-1 text-xs text-white focus:outline-none focus:border-indigo-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-[10px] text-slate-500 uppercase font-medium">Fin</span>
                        <input type="datetime-local" value={slot.end}
                          onChange={(e) => { const s = [...newPoint.openingHours]; s[idx].end = e.target.value; setNewPoint({...newPoint, openingHours: s}); }}
                          className={`w-full bg-[#14161B] border rounded p-2 mt-1 text-xs text-white focus:outline-none focus:border-indigo-500 ${slot.end && slot.start && slot.end < slot.start ? 'border-red-500' : 'border-[#2D3139]'}`} />
                        {slot.end && slot.start && slot.end < slot.start && (
                          <span className="text-[10px] text-red-400 mt-0.5 block">⚠ Date de fin invalide</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="text-[10px] text-slate-500 uppercase font-medium">Team Requis (Ce créneau)</span>
                        <input type="number" min="1" max="50" value={slot.requiredTeam || newPoint.requiredTeam || 1}
                          onChange={(e) => { const s = [...newPoint.openingHours]; s[idx].requiredTeam = parseInt(e.target.value) || 1; setNewPoint({...newPoint, openingHours: s}); }}
                          className="w-full bg-[#14161B] border border-[#2D3139] rounded p-2 mt-1 text-xs text-white focus:outline-none focus:border-indigo-500" />
                      </div>
                      <div className="flex-1 pl-4 pt-4">
                        <label className="flex items-center gap-2 cursor-pointer group/hard">
                          <input 
                            type="checkbox" 
                            checked={slot.isHard} 
                            onChange={(e) => { const s = [...newPoint.openingHours]; s[idx].isHard = e.target.checked; setNewPoint({...newPoint, openingHours: s}); }}
                            className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-amber-500 focus:ring-amber-500/20"
                          />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 group-hover/hard:text-amber-400 transition-colors flex items-center gap-1">
                            <Flame className={`w-3 h-3 ${slot.isHard ? 'text-amber-500 animate-pulse' : 'text-slate-600'}`} />
                            Pénibilité
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
                {newPoint.openingHours.length === 0 && (
                  <div className="text-xs text-amber-500/70 bg-amber-500/5 p-4 border border-dashed border-amber-500/20 rounded-xl text-center flex flex-col items-center gap-2">
                    <CalendarDays className="w-5 h-5 opacity-50" />
                    <span>Aucun créneau configuré. Le point sera considéré comme fermé.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#2D3139]/50">
              <button onClick={closeModal} 
                className="px-5 py-2.5 border border-[#2D3139] hover:border-slate-600 hover:bg-[#161920] text-slate-300 hover:text-white rounded-xl transition-all flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider">
                <X className="w-3.5 h-3.5" /> Annuler
              </button>
              <button onClick={submitNewPoint} disabled={!newPoint.name.trim()}
                className={`px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider ${editingId ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 shadow-indigo-500/20' : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 shadow-emerald-500/20'}`}>
                {editingId ? <Edit2 className="w-3.5 h-3.5"/> : <Plus className="w-3.5 h-3.5"/>}
                {editingId ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </ConfigModal>

        {/* Multi-slot picker for bulk slot creation */}
        {multiSlotPickerOpen && (
          <MultiSlotPicker
            pointName={newPoint.name || 'Nouveau point'}
            existingDates={new Set(newPoint.openingHours.map(s => s.start.split('T')[0]))}
            onAdd={(slots) => {
              setNewPoint({
                ...newPoint,
                openingHours: [...newPoint.openingHours, ...slots],
              });
              setMultiSlotPickerOpen(false);
            }}
            onClose={() => setMultiSlotPickerOpen(false)}
          />
        )}

        {/* List */}
        <div className="flex-1 bg-transparent sm:bg-[#14161B]/50 backdrop-blur-none sm:backdrop-blur-sm border-0 sm:border border-[#2D3139] rounded-none sm:rounded-2xl overflow-y-auto p-0 sm:p-6 custom-scrollbar relative shadow-none sm:shadow-inner z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sticky top-0 bg-[#14161B] sm:bg-[#14161B]/90 backdrop-blur-md p-2 -mt-2 sm:-mt-2 sm:-mx-2 rounded-lg z-20 border-b border-[#2D3139]/30 pb-3">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-lg text-white">Points Existants</h3>
              <span className="bg-indigo-500/20 text-indigo-400 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/30">
                {visiblePoints.length} {searchQuery ? 'trouvé(s)' : 'total'}
              </span>
            </div>
            
            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher un point..."
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-8 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-medium"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          {visiblePoints.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4">
              <PointIcon name="MapPin" size={64} className="opacity-20" />
              <p>{searchQuery ? "Aucun résultat pour cette recherche." : "Aucun point opérationnel configuré."}</p>
            </div>
          ) : (
            <div className="space-y-0">
              {groupPointsByCategory(visiblePoints, categories, { effectiveCategoryId: effectiveCategoryId ?? undefined, isArtistManager: isArtistCategoryManager === true }).map(({ category, points }) => {
                const isOpen = searchQuery ? true : !closedDrawers[category.id];
                return (
                  <CategoryDrawer
                    key={category.id}
                    category={category}
                    pointCount={points.length}
                    isOpen={isOpen}
                    onToggle={() => setClosedDrawers(prev => ({ ...prev, [category.id]: !prev[category.id] }))}
                  >
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {points.map(point => (
                <div key={point.id} className={`group relative border bg-[#0A0B0E]/80 backdrop-blur-sm p-5 rounded-2xl flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden ${
                  point.isStage 
                    ? 'border-amber-500/40 hover:border-amber-500/70 hover:shadow-amber-500/5' 
                    : 'border-[#2D3139] hover:border-[#3D414D] hover:shadow-indigo-500/5'
                }`}>
                   <div className="flex justify-between items-start z-10">
                     <div className="flex items-center gap-4">
                        <div className={`p-3 bg-gradient-to-br from-[#1A1D23] to-[#14161B] rounded-xl shadow-inner relative ${
                          point.isStage 
                            ? 'text-amber-400 border border-amber-500/30' 
                            : 'text-indigo-400 border border-[#2D3139]'
                        }`}>
                          <PointIcon name={point.iconName || 'MapPin'} size={24} />
                          {point.isRestricted && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-600 rounded-full flex items-center justify-center border-2 border-[#0A0B0E] shadow-lg" title="Point à accès restreint">
                              <Lock className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-lg tracking-wide flex items-center gap-2">
                            {point.name}
                          </h4>
                          <p className="text-xs text-slate-400 mt-0.5">Team min. requis: <span className="text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded">{point.requiredTeam}</span></p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {point.isStage && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Music className="w-2.5 h-2.5" /> Scène / Stage
                              </span>
                            )}
                            {point.swapEnabled && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">Échanges ✓</span>
                            )}
                            {point.unavailabilityEnabled && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full">Indispo ✓</span>
                            )}
                            {point.isMapFocus && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-2 py-0.5 rounded-full">Focus Map</span>
                            )}
                            {point.isMealPoint && (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full">Repas 🍴</span>
                            )}
                          </div>
                        </div>
                     </div>
                     <div className="flex items-center space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-[#14161B] p-1 rounded-lg border border-[#2D3139]">
                       <button 
                         onClick={() => setQrModalPoint(point)}
                         className="text-amber-400 hover:bg-amber-500/20 p-1.5 rounded transition-colors text-sm flex items-center"
                         title="QR Code Pointage"
                       >
                         <QrCode className="w-4 h-4" />
                       </button>
                       {(currentUser.role === 'admin' || point.categoryId === currentUser.categoryId) && (
                         <>
                           <button 
                             onClick={() => setSettingsPoint(point)}
                             className="text-slate-400 hover:bg-slate-500/20 p-1.5 rounded transition-colors text-sm flex items-center hover:text-white"
                             title="Options"
                           >
                             <Settings className="w-4 h-4" />
                           </button>
                           <button 
                             onClick={() => startEdit(point)}
                             className="text-indigo-400 hover:bg-indigo-500/20 p-1.5 rounded transition-colors text-sm flex items-center"
                             title="Modifier"
                           >
                             <Edit2 className="w-4 h-4" />
                           </button>
                           <button 
                             onClick={() => {
                               if (window.confirm('Êtes-vous sûr de vouloir supprimer ce point ? Cette action est irréversible.')) {
                                 deletePoint(point.id);
                               }
                             }}
                             className="text-red-400 hover:bg-red-500/20 p-1.5 rounded transition-colors text-sm"
                             title="Supprimer"
                           >
                             <Trash2 className="w-4 h-4" />
                           </button>
                         </>
                       )}
                     </div>
                   </div>
                   
                   <div className="mt-5 pt-4 border-t border-[#2D3139]/50 z-10">
                     <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                       <CalendarDays className="w-3.5 h-3.5" /> Créneaux d'Ouverture ({point.openingHours.length})
                     </p>
                     <div className="grid grid-cols-1 gap-2">
                       {[...point.openingHours].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()).map((slot, i) => {
                         const startDt = new Date(slot.start);
                         const endDt = new Date(slot.end);
                         return (
                           <div key={i} onClick={() => setEditingSlot({ pointId: point.id, idx: i, start: slot.start, end: slot.end, requiredTeam: slot.requiredTeam || point.requiredTeam, originalStart: slot.start, originalEnd: slot.end })}
                             className="bg-[#14161B] border border-[#2D3139] p-2.5 rounded-lg text-xs flex justify-between items-center group/slot hover:border-indigo-500/30 transition-colors cursor-pointer" title="Cliquer pour modifier ce créneau">
                             <div className="flex items-center gap-2">
                               <div className="flex flex-col">
                                 <span className="text-white font-medium">{startDt.toLocaleString(undefined, {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}</span>
                               </div>
                               <ArrowRight className="w-3 h-3 text-slate-500" />
                               <div className="flex flex-col">
                                 <span className="text-white font-medium">{endDt.toLocaleString(undefined, {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}</span>
                               </div>
                             </div>
                             <div className="flex items-center gap-2">
                               {slot.requiredTeam !== point.requiredTeam && (
                                 <span className="text-[10px] bg-[#2D3139] text-slate-300 px-1.5 py-0.5 rounded font-medium">Team: {slot.requiredTeam}</span>
                               )}
                               {slot.isHard && (
                                  <Flame className="w-3 h-3 text-amber-500" />
                               )}
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   const slotShifts = shifts.filter(sh => sh.pointId === point.id && sh.start < slot.end && sh.end > slot.start);
                                   const msg = slotShifts.length > 0
                                     ? `Ce créneau a ${slotShifts.length} team assigné(s). Supprimer le créneau supprimera aussi leurs shifts. Continuer ?`
                                     : 'Supprimer ce créneau ?';
                                   if (!window.confirm(msg)) return;
                                   const realIdx = point.openingHours.findIndex(oh => oh.start === slot.start && oh.end === slot.end);
                                   if (realIdx === -1) return;
                                   const filtered = point.openingHours.filter((_, j) => j !== realIdx);
                                   updatePoint(point.id, { openingHours: filtered });
                                 }}
                                 className="opacity-0 group-hover/slot:opacity-100 p-1 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-all"
                                 title="Supprimer ce créneau"
                               >
                                 <X className="w-3 h-3" />
                               </button>
                             </div>
                           </div>
                         )
                       })}
                     </div>
                   </div>
                </div>
              ))}
                    </div>
                  </CategoryDrawer>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalPoint && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[110]">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#2D3139] flex justify-between items-center bg-[#1A1D23]">
              <h3 className="font-semibold text-white">QR Code de Pointage</h3>
              <button onClick={() => { setQrModalPoint(null); setPrintPngUrl(null); }} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-8 flex flex-col items-center justify-center bg-white" id={`qr-code-${qrModalPoint.id}`}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{qrModalPoint.name}</h2>
              <div className="bg-white p-4 rounded-xl border-4 border-indigo-600 shadow-xl">
                 <QRCode value={`myapp://checkin/${qrModalPoint.id}`} size={200} level="H" />
              </div>
              <p className="text-gray-600 font-medium text-center mt-6">
                Scannez ce QR Code avec l'application pour valider votre présence sur ce poste.
              </p>
            </div>
            <div className="p-4 border-t border-[#2D3139] flex justify-center bg-[#1A1D23]">
               <button 
                 onClick={() => {
                      const qrCodeContainer = document.getElementById(`qr-code-${qrModalPoint.id}`);
                      const svgElement = qrCodeContainer?.querySelector('svg');
                      if (svgElement) {
                        // Convert SVG to PNG via Canvas for thermal printer compatibility
                        const svgString = new XMLSerializer().serializeToString(svgElement);
                        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                        const URL = window.URL || window.webkitURL || window;
                        const blobUrl = URL.createObjectURL(svgBlob);
                        
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement('canvas');
                          canvas.width = 400;
                          canvas.height = 400;
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            // Draw with white background and black QR code
                            ctx.fillStyle = '#ffffff';
                            ctx.fillRect(0, 0, 400, 400);
                            ctx.drawImage(img, 0, 0, 400, 400);
                            
                            const pngUrl = canvas.toDataURL('image/png');
                            setPrintPngUrl(pngUrl);
                            
                            // Let React render the Portal, then trigger print
                            setTimeout(() => {
                              window.focus();
                              window.print();
                            }, 500);
                          }
                          URL.revokeObjectURL(blobUrl);
                        };
                        img.src = blobUrl;
                      }
                    }}
                 className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-colors w-full"
               >
                 Imprimer
               </button>
            </div>
          </div>
        </div>
      )}

      {printPngUrl && createPortal(
        <div id="thermal-print-section">
          <div className="ticket-container">
            <h1 className="title">{qrModalPoint?.name}</h1>
            <div className="qr-container">
              <img src={printPngUrl} alt="QR Code" />
            </div>
            <p className="footer">
              Scannez ce QR Code avec l'application pour valider votre présence sur ce poste.
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Point Options Settings Modal */}
      {settingsPoint && (() => {
        const cat = categories.find(c => c.id === settingsPoint.categoryId);
        const isSwapEnabled = settingsPoint.swapEnabled === true;
        return (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[110] backdrop-blur-sm">
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl">
              <div className="p-4 border-b border-[#2D3139] flex justify-between items-center bg-[#1A1D23]">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-indigo-400 animate-spin-slow" />
                  Options : {settingsPoint.name}
                </h3>
                <button onClick={() => setSettingsPoint(null)} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-[#2D3139] transition-all">
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-5 bg-[#0A0B0E]/95">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Configurez le comportement de ce point opérationnel pour vos équipes de team.
                </p>
                
                <div className="space-y-4 pt-2">
                  {/* Swap Toggle */}
                  <div className="flex items-center justify-between bg-[#14161B] p-4 rounded-xl border border-[#2D3139]/80">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-white">Autoriser les Échanges</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Permet aux teams de s'échanger leurs shifts.</span>
                    </div>
                    <button
                      onClick={() => {
                        const updated = { ...settingsPoint, swapEnabled: !isSwapEnabled };
                        updatePoint(settingsPoint.id, { swapEnabled: !isSwapEnabled });
                        setSettingsPoint(updated);
                      }}
                      className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                        isSwapEnabled ? 'bg-emerald-500' : 'bg-[#2D3139]'
                      }`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        isSwapEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>

                  {/* Unavailability Toggle */}
                  <div className="flex items-center justify-between bg-[#14161B] p-4 rounded-xl border border-[#2D3139]/80">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-white">Activer Indisponibilités</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Laisse le team déclarer ses jours indisponibles.</span>
                    </div>
                    <button
                      onClick={() => {
                        const updated = { ...settingsPoint, unavailabilityEnabled: !settingsPoint.unavailabilityEnabled };
                        updatePoint(settingsPoint.id, { unavailabilityEnabled: !settingsPoint.unavailabilityEnabled });
                        setSettingsPoint(updated);
                      }}
                      className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                        settingsPoint.unavailabilityEnabled ? 'bg-indigo-500' : 'bg-[#2D3139]'
                      }`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        settingsPoint.unavailabilityEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>

                  {/* Map Focus Toggle */}
                  <div className="flex items-center justify-between bg-[#14161B] p-4 rounded-xl border border-[#2D3139]/80">
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-white">Mise en avant (Focus Map)</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Met en valeur ce point sur la carte en temps réel.</span>
                    </div>
                    <button
                      onClick={() => {
                        const updated = { ...settingsPoint, isMapFocus: !settingsPoint.isMapFocus };
                        updatePoint(settingsPoint.id, { isMapFocus: !settingsPoint.isMapFocus });
                        setSettingsPoint(updated);
                      }}
                      className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                        settingsPoint.isMapFocus ? 'bg-cyan-500' : 'bg-[#2D3139]'
                      }`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        settingsPoint.isMapFocus ? 'translate-x-5' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>

                  {/* Meal Point Toggle (if general category) */}
                  {cat?.isGeneral && (
                    <div className="flex items-center justify-between bg-[#14161B] p-4 rounded-xl border border-[#2D3139]/80">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-white">Point Repas (Déj / Dîner)</span>
                        <span className="text-[10px] text-slate-500 mt-0.5">Considéré comme repas pour le calcul des repas manqués.</span>
                      </div>
                      <button
                        onClick={() => {
                          const updated = { ...settingsPoint, isMealPoint: !settingsPoint.isMealPoint };
                          updatePoint(settingsPoint.id, { isMealPoint: !settingsPoint.isMealPoint });
                          setSettingsPoint(updated);
                        }}
                        className={`flex-shrink-0 relative w-11 h-6 rounded-full transition-colors focus:outline-none ${
                          settingsPoint.isMealPoint ? 'bg-amber-500' : 'bg-[#2D3139]'
                        }`}
                      >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          settingsPoint.isMealPoint ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4 border-t border-[#2D3139] flex justify-center bg-[#1A1D23]">
                <button
                  onClick={() => setSettingsPoint(null)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-bold transition-all w-full shadow-lg shadow-indigo-500/20"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Slot Editor Modal */}
      {editingSlot && (() => {
        const point = points.find(p => p.id === editingSlot.pointId);
        const handleSaveSlot = () => {
          if (!point) return;
          // Find the real index by matching start time (the display uses sorted array, original may differ)
          const realIdx = point.openingHours.findIndex(oh => oh.start === editingSlot.originalStart && oh.end === editingSlot.originalEnd);
          if (realIdx === -1) { setEditingSlot(null); return; }
          const updated = [...point.openingHours];
          updated[realIdx] = { ...updated[realIdx], start: editingSlot.start, end: editingSlot.end, requiredTeam: editingSlot.requiredTeam };
          updatePoint(point.id, { openingHours: updated });
          setEditingSlot(null);
        };
        const handleDeleteSlot = () => {
          if (!point) return;
          const slotShifts = shifts.filter(sh => sh.pointId === point.id && sh.start < editingSlot.originalEnd && sh.end > editingSlot.originalStart);
          const msg = slotShifts.length > 0
            ? `Ce créneau a ${slotShifts.length} team assigné(s). Supprimer le créneau supprimera aussi leurs shifts. Continuer ?`
            : 'Supprimer ce créneau ?';
          if (!window.confirm(msg)) return;
          const realIdx = point.openingHours.findIndex(oh => oh.start === editingSlot.originalStart && oh.end === editingSlot.originalEnd);
          if (realIdx === -1) { setEditingSlot(null); return; }
          const filtered = point.openingHours.filter((_, i) => i !== realIdx);
          updatePoint(point.id, { openingHours: filtered });
          setEditingSlot(null);
        };
        return (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="text-white font-semibold mb-1">Modifier le créneau</h3>
              <p className="text-slate-500 text-xs mb-4">{point?.name}</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Début</label>
                  <input type="datetime-local" value={editingSlot.start.substring(0, 16)} onChange={e => setEditingSlot({ ...editingSlot, start: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Fin</label>
                  <input type="datetime-local" value={editingSlot.end.substring(0, 16)} onChange={e => setEditingSlot({ ...editingSlot, end: e.target.value })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Team requis</label>
                  <input type="number" min={0} value={editingSlot.requiredTeam} onChange={e => setEditingSlot({ ...editingSlot, requiredTeam: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-5">
                <button onClick={handleDeleteSlot}
                  className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors">
                  Supprimer
                </button>
                <div className="flex-1" />
                <button onClick={() => setEditingSlot(null)}
                  className="px-3 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-300 rounded-lg text-xs font-medium transition-colors">
                  Annuler
                </button>
                <button onClick={handleSaveSlot}
                  className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-bold transition-colors">
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
